import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, remove } from 'firebase/database';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    FaSearch, 
    FaFileAlt, 
    FaAddressCard, 
    FaUserCircle, 
    FaTrashAlt, 
    FaUpload, 
    FaCopy,
    FaFileExcel,
    FaPrint,
    FaArrowLeft
} from 'react-icons/fa';

const ZpHeadSocieties = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Determine title based on path
    const isNew = location.pathname.includes('/new');
    const pageTitle = isNew ? "नवीन मजूर संस्थांची यादी" : "जुने मजूर संस्थांची यादी";

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/SocietyRegistrations');
        const unsubData = onValue(registrationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                // Real-time data from Firebase
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const filteredData = registrations.filter(item => {
        const fullName = `${item.societyName || ''} ${item.chairmanName || ''}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase());
    });

    const getStatusText = (status) => {
        if (!status || status === 'pending') return <span className="text-amber-500 font-bold uppercase tracking-tighter">Pending</span>;
        if (status === 'accepted') return <span className="text-green-600 font-bold uppercase tracking-tighter">Accepted</span>;
        if (status === 'rejected') return <span className="text-red-600 font-bold uppercase tracking-tighter">Rejected</span>;
        return <span className="text-amber-500 font-bold uppercase tracking-tighter">Pending</span>;
    };
    
    const handleDelete = async (id) => {
        if (window.confirm("तुम्हाला खात्री आहे की तुम्ही ही नोंदणी हटवू इच्छिता?")) {
            try {
                const societyRef = ref(db, `ekamwatapzp/SocietyRegistrations/${id}`);
                await remove(societyRef);
                toast.success("नोंदणी यशस्वीरीत्या हटवण्यात आली!");
            } catch (error) {
                toast.error("त्रुटी: " + error.message);
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans text-inter">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="p-2.5 bg-white border border-gray-200 rounded-xl shadow-sm text-gray-600 hover:text-blue-600 hover:border-blue-100 hover:bg-blue-50/50 transition-all duration-300 active:scale-95 group"
                        title="मागे जा"
                    >
                        <FaArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800 border-l-4 border-[#1a1a4d] pl-4">{pageTitle}</h1>
                </div>
                <div className="text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400">{pageTitle}</span>
                </div>
            </div>

            {/* Content Area */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                
                {/* Stats & Legends Header */}
                <div className="p-6 border-b border-gray-50 bg-white flex flex-col lg:flex-row justify-between gap-6">
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-amber-500 shadow-sm shadow-amber-100"></span>
                            <span className="text-xs font-bold text-gray-600 tracking-tight">निसंबित</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-600 shadow-sm shadow-green-100"></span>
                            <span className="text-xs font-bold text-gray-600 tracking-tight">मंजूर</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-red-600 shadow-sm shadow-red-100"></span>
                            <span className="text-xs font-bold text-gray-600 tracking-tight">नाकारते</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        <div className="flex items-center gap-2"><FaAddressCard className="text-gray-700 text-sm" /> अर्जदाराची संपूर्ण माहिती</div>
                        <div className="flex items-center gap-2"><FaUserCircle className="text-gray-700 text-sm" /> अर्जदाराची कागदपत्रे</div>
                        <div className="flex items-center gap-2"><FaTrashAlt className="text-gray-400 text-sm" /> डिलीट</div>
                    </div>
                </div>

                {/* Toolbar Section */}
                <div className="p-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-700 transition active:scale-95"><FaCopy className="text-xs" /> Copy</button>
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-700 transition active:scale-95"><FaFileExcel className="text-xs" /> Excel</button>
                        <button className="flex items-center gap-2 px-5 py-2.5 bg-[#6c757d] text-white rounded text-sm font-bold shadow-md hover:bg-gray-700 transition active:scale-95"><FaPrint className="text-xs" /> Print</button>
                    </div>

                    <div className="flex items-center gap-4">
                        <span className="text-sm font-bold text-gray-500 flex-shrink-0">Search:</span>
                        <div className="relative">
                            <input 
                                type="text" 
                                className="w-64 pl-4 pr-10 py-2.5 border-2 border-gray-100 rounded-lg text-sm font-medium focus:border-[#1a1a4d] transition-all outline-none bg-gray-50"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <FaSearch className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300" />
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-[#f8f9fa] border-y border-gray-100 text-center">
                            <tr>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r">क्र. ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r min-w-[250px] text-left">संस्थेचे नाव ↑↓</th>
                                {isNew && <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r">फोन क्र. ↑↓</th>}
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r">नोंदणी दिनांक ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center text-amber-500 italic">Assistant Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center text-green-600 italic">Accountant Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center text-blue-600 italic">Engineer Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center text-red-600 italic">Add CEO Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest border-r text-center text-purple-600 italic">CEO Status ↑↓</th>
                                <th className="px-6 py-5 text-[11px] font-black text-gray-400 uppercase tracking-widest text-center">Action ↑↓</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredData.map((item, index) => (
                                <tr 
                                    key={item.id} 
                                    className={`group transition-all ${!isNew && index % 2 === 1 ? 'bg-[#f4f7fe]' : 'bg-white'} hover:bg-blue-50/40`}
                                >
                                    <td className="px-6 py-6 text-sm font-black text-gray-400 border-r text-center italic">{index + 1}</td>
                                    <td className="px-6 py-6 border-r text-left">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="font-bold text-gray-800 text-[15px] group-hover:text-blue-700 transition line-clamp-2">
                                                {item.societyName}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{item.taluka}</span>
                                                {!isNew && (
                                                    <span className="text-[11px] font-bold text-gray-400 tracking-wider">
                                                        • {item.chairmanWhatsapp || item.contactNo}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    {isNew && <td className="px-6 py-6 text-sm font-bold text-gray-600 border-r tracking-wider text-center">{item.chairmanWhatsapp || item.contactNo}</td>}
                                    <td className="px-6 py-6 text-sm font-bold text-gray-500 border-r text-center leading-relaxed">
                                        {item.submittedAt ? (
                                            <div className="flex flex-col">
                                                <span>{new Date(item.submittedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-')}</span>
                                                <span className="text-[10px] text-gray-400">
                                                    {new Date(item.submittedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                                                </span>
                                            </div>
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.assistantStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.accountantStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.engineerStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.adminStatus)}</td>
                                    <td className="px-6 py-6 text-center border-r">{getStatusText(item.status)}</td>
                                    <td className="px-6 py-6 font-bold">
                                        <div className="flex items-center gap-4 justify-center">
                                            <FaAddressCard 
                                                className="text-gray-400 text-lg cursor-pointer hover:text-blue-600 hover:scale-110 transition shrink-0" 
                                                title="संपूर्ण माहिती" 
                                                onClick={() => navigate(`/zp-head/societies/details/${item.id}`)}
                                            />
                                            <FaTrashAlt 
                                                className="text-gray-300 text-lg cursor-pointer hover:text-red-500 hover:scale-110 transition shrink-0" 
                                                title="डिलीट" 
                                                onClick={() => handleDelete(item.id)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    
                    {filteredData.length === 0 && !loading && (
                        <div className="py-20 text-center flex flex-col items-center gap-4">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center">
                                <FaSearch className="text-gray-200 text-3xl" />
                            </div>
                            <p className="text-gray-300 font-black uppercase tracking-widest italic">कोणतीही नोंदणी आढळली नाही.</p>
                        </div>
                    )}
                </div>

                {/* Table Footer */}
                <div className="p-6 border-t border-gray-50 bg-gray-50/10 flex justify-between items-center text-xs font-bold text-gray-400 tracking-wider">
                    <div>Showing {filteredData.length} entries</div>
                </div>
            </div>
        </div>
    );
};

export default ZpHeadSocieties;
